import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tag,
  Text,
} from '@chakra-ui/react';
import {
  AddOfferCurrenciesRequest,
  MissingBranchDetail,
  offerApi,
} from '@texas/api/endpoints/offerApi';
import { currenciesApi } from '@texas/api/endpoints/currenciesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BranchSelection {
  branchId: number;
  selectedCurrencyIds: number[];
  selectedInquiryId: number | null;
}

interface Props {
  offerGroupId: number;
  customerId: number;
  missingBranches: MissingBranchDetail[];
  existingMatchedInquiryIds: number[];
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export function AddBranchToOfferModal({
  offerGroupId,
  customerId,
  missingBranches,
  existingMatchedInquiryIds,
  isOpen,
  onClose,
  onAdded,
}: Props) {
  const { t } = useTranslation();

  const [selections, setSelections] = useState<BranchSelection[]>([]);

  const {
    data: allCurrencies,
    loading: currenciesLoading,
    refetch: refetchCurrencies,
  } = useApiResource(
    currenciesApi.getAllCurrenciesMergedWithCustomerCurrencies,
  );

  const { request: addCurrencies, loading: adding } = useApiRequest(
    offerApi.addCurrencies,
  );
  const { request: setMatchingInquiry } = useApiRequest(
    offerApi.setMatchingInquiry,
  );

  // Initialise selections when modal opens
  useEffect(() => {
    if (!isOpen) return;
    refetchCurrencies(customerId);
    setSelections(
      missingBranches.map((b) => ({
        branchId: b.branchId,
        selectedCurrencyIds: [b.branchDefaultCurrencyId],
        selectedInquiryId:
          b.acceptedInquiries.length === 1
            ? b.acceptedInquiries[0].inquiryId
            : b.acceptedInquiries.find((i) => i.isMain)?.inquiryId ?? null,
      })),
    );
  }, [isOpen, missingBranches, customerId, refetchCurrencies]);

  const updateSelection = (
    branchId: number,
    patch: Partial<Omit<BranchSelection, 'branchId'>>,
  ) => {
    setSelections((prev) =>
      prev.map((s) => (s.branchId === branchId ? { ...s, ...patch } : s)),
    );
  };

  const toggleCurrency = (
    branchId: number,
    currencyId: number,
    defaultCurrencyId: number,
  ) => {
    if (currencyId === defaultCurrencyId) return; // locked
    const sel = selections.find((s) => s.branchId === branchId);
    if (!sel) return;
    const already = sel.selectedCurrencyIds.includes(currencyId);
    updateSelection(branchId, {
      selectedCurrencyIds: already
        ? sel.selectedCurrencyIds.filter((id) => id !== currencyId)
        : [...sel.selectedCurrencyIds, currencyId],
    });
  };

  const toggleInquiry = (branchId: number, inquiryId: number) => {
    const sel = selections.find((s) => s.branchId === branchId);
    if (!sel) return;
    updateSelection(branchId, {
      selectedInquiryId: sel.selectedInquiryId === inquiryId ? null : inquiryId,
    });
  };

  const hasAnySelection = selections.some(
    (s) => s.selectedCurrencyIds.length > 0,
  );

  const handleConfirm = async () => {
    for (const sel of selections) {
      if (sel.selectedCurrencyIds.length === 0) continue;
      const req: AddOfferCurrenciesRequest = {
        branchId: sel.branchId,
        currencyIds: sel.selectedCurrencyIds,
      };
      await addCurrencies(offerGroupId, req);
    }

    const newInquiryIds = selections
      .map((s) => s.selectedInquiryId)
      .filter((id): id is number => id !== null);

    if (newInquiryIds.length > 0) {
      const merged = [
        ...new Set([...existingMatchedInquiryIds, ...newInquiryIds]),
      ];
      await setMatchingInquiry(offerGroupId, merged);
    }

    onAdded();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('offer.addBranchesToOffer')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {currenciesLoading ? (
            <Spinner size="sm" />
          ) : (
            <Flex direction="column" gap={4}>
              {missingBranches.map((branch) => {
                const sel = selections.find(
                  (s) => s.branchId === branch.branchId,
                );
                const branchCurrencies = (allCurrencies ?? []).filter(
                  (c) => c.branchId === branch.branchId,
                );

                return (
                  <Card key={branch.branchId} variant="dark">
                    <CardHeader>
                      <Flex align="center" gap={2}>
                        <Text fontWeight="semibold">{branch.branchName}</Text>
                        <Tag size="sm" colorScheme="gray">
                          {branch.branchIdentifier}
                        </Tag>
                      </Flex>
                    </CardHeader>
                    <CardBody>
                      {/* Inquiry picker */}
                      {branch.acceptedInquiries.length > 0 && (
                        <Box mb={4}>
                          <Text
                            fontSize="xs"
                            color="gray.400"
                            mb={2}
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            {t('offer.selectInquiryForBranch')}
                          </Text>
                          <Flex direction="column" gap={2}>
                            {branch.acceptedInquiries.map((inq) => {
                              const isSelected =
                                sel?.selectedInquiryId === inq.inquiryId;
                              return (
                                <Flex
                                  key={inq.inquiryId}
                                  borderWidth="1px"
                                  borderRadius="md"
                                  px={3}
                                  py={2}
                                  cursor="pointer"
                                  gap={2}
                                  align="center"
                                  bg={isSelected ? 'blue.900' : 'whiteAlpha.50'}
                                  borderColor={
                                    isSelected ? 'blue.500' : 'whiteAlpha.100'
                                  }
                                  _light={{
                                    bg: isSelected ? 'blue.50' : undefined,
                                    borderColor: isSelected
                                      ? 'blue.400'
                                      : undefined,
                                  }}
                                  onClick={() =>
                                    toggleInquiry(
                                      branch.branchId,
                                      inq.inquiryId,
                                    )
                                  }
                                >
                                  <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    flex="1"
                                  >
                                    {inq.inquiryNumber}
                                  </Text>
                                  {inq.isMain && (
                                    <Badge colorScheme="green" fontSize="2xs">
                                      {t('offer.main')}
                                    </Badge>
                                  )}
                                  <Flex gap={1} wrap="wrap">
                                    {inq.tierPrices.map((tp) => (
                                      <Tag
                                        key={tp.quantity}
                                        size="sm"
                                        colorScheme="gray"
                                        variant="subtle"
                                      >
                                        {tp.quantity}
                                      </Tag>
                                    ))}
                                  </Flex>
                                </Flex>
                              );
                            })}
                          </Flex>
                        </Box>
                      )}

                      {/* Currency picker */}
                      <Box>
                        <Text
                          fontSize="xs"
                          color="gray.400"
                          mb={2}
                          textTransform="uppercase"
                          letterSpacing="wide"
                        >
                          {t('general.currency')}
                        </Text>
                        {branchCurrencies.length === 0 ? (
                          <Text fontSize="sm" color="gray.500">
                            {t('offer.noCurrenciesAvailable')}
                          </Text>
                        ) : (
                          <Flex gap={2} wrap="wrap">
                            {branchCurrencies.map((c) => {
                              const isDefault =
                                c.id === branch.branchDefaultCurrencyId;
                              const isSelected =
                                sel?.selectedCurrencyIds.includes(c.id) ??
                                false;
                              return (
                                <Tag
                                  key={c.id}
                                  size="md"
                                  colorScheme={isSelected ? 'blue' : 'gray'}
                                  variant={isSelected ? 'solid' : 'subtle'}
                                  cursor={isDefault ? 'not-allowed' : 'pointer'}
                                  opacity={isDefault ? 0.8 : 1}
                                  onClick={() =>
                                    toggleCurrency(
                                      branch.branchId,
                                      c.id,
                                      branch.branchDefaultCurrencyId,
                                    )
                                  }
                                >
                                  {c.code}
                                </Tag>
                              );
                            })}
                          </Flex>
                        )}
                      </Box>
                    </CardBody>
                  </Card>
                );
              })}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('general.cancel')}
          </Button>
          <Button
            variant="texas-solid"
            size="sm"
            isLoading={adding}
            isDisabled={!hasAnySelection}
            onClick={handleConfirm}
          >
            {t('general.add')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
